import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../../services/api';
import { AlimentoCommercialeDettaglio, Categoria, AlimentiFiltri, ApiResponse } from '../../../types/alimenti';
import Accordion from '../../controls/Accordion';
import PaginationControls from '../../controls/PaginationControls';
import SortIcon from '../../controls/SortIcon';
import useAccordion from '../../../hooks/useAccordion';
import usePagination from '../../../hooks/usePagination';
import useSort from '../../../hooks/useSort';
import './AlimentiPersonaliDettaglio.css';
import './AlimentiCommercialiDettaglio.css';


const AlimentiCommercialiDettaglio: React.FC = () => {
  const navigate = useNavigate();
  const [risultati, setRisultati] = useState<AlimentoCommercialeDettaglio[]>([]);
  const [categorie, setCategorie] = useState<Categoria[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Hook per ordinamento
  const { sortedData, sortConfig, handleSort } = useSort(risultati, {
    initialField: 'dataUtilizzo',
    initialDirection: 'desc'
  });
  
  // Hook per paginazione
  const {
    paginatedData,
    paginationInfo,
    goToPage,
    goToFirstPage,
    goToPreviousPage, 
    goToNextPage,
    goToLastPage,
    changeItemsPerPage,
    resetToFirstPage
  } = usePagination(sortedData, { initialItemsPerPage: 10 });
  
  // Stato per modal immagini
  const [modalImage, setModalImage] = useState<{src: string, alt: string, extraImages?: string[]} | null>(null);
  
  // Filtri
  const [filtri, setFiltri] = useState<AlimentiFiltri>({
    dataDa: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0], // 30 giorni fa
    dataA: new Date().toISOString().split('T')[0], // oggi
    tipo: 'tutti',
    idCategoria: null
  });
  
  // Hook per accordion
  const filtersAccordion = useAccordion(true);

  // Carica categorie al mount
  useEffect(() => {
    caricaCategorie();
  }, []);

  // Gestione chiusura modal con ESC
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && modalImage) {
        closeImageModal();
      }
    };

    if (modalImage) {
      document.addEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'hidden'; // Blocca scroll del body
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = 'unset';
    };
  }, [modalImage]);

  const caricaCategorie = async () => {
    try {
      const response = await api.get<ApiResponse<Categoria[]>>('/AlimentiCommerciali/categorie');
      if (response.data.success) {
        setCategorie(response.data.data);
      }
    } catch (error) {
      console.error('Errore nel caricamento categorie:', error);
    }
  };

  const cercaAlimenti = async () => {
    setLoading(true);
    setError(null);
    
    try {
      const payload = {
        dataDa: filtri.dataDa,
        dataA: filtri.dataA,
        tipo: filtri.tipo,
        idCategoria: filtri.idCategoria || undefined
      };

      const response = await api.post<ApiResponse<AlimentoCommercialeDettaglio[]>>('/AlimentiCommerciali/dettaglio', payload);
      
      if (response.data.success) {
        setRisultati(response.data.data);
        resetToFirstPage(); // Reset alla prima pagina per nuova ricerca
      } else {
        setError(response.data.message || 'Errore nella ricerca');
      }
    } catch (error: any) {
      setError(error.response?.data?.message || 'Errore durante la ricerca');
      console.error('Errore ricerca alimenti commerciali dettaglio:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFiltroChange = (campo: keyof AlimentiFiltri, valore: any) => {
    setFiltri(prev => ({
      ...prev,
      [campo]: valore
    }));
  };


  // Gestione modal immagini
  const openImageModal = (src: string, alt: string, pic2?: string) => {
    const extraImages = pic2 ? [pic2] : [];
    setModalImage({ src, alt, extraImages });
  };

  const closeImageModal = () => {
    setModalImage(null);
  };


  // Funzione per formattare la data
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('it-IT', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Funzione per formattare valori nutrizionali
  const formatNutritional = (value: number) => {
    return value ? value.toFixed(1) : '0.0';
  };

  // Calcoli statistiche
  const totaleUtilizzi = risultati.length;
  const utentiUnici = new Set(risultati.map(item => item.idUser)).size;
  const alimentiUnici = new Set(risultati.map(item => item.idAlimento)).size;

  return (
    <div className="alimenti-personali-container">
      <div className="page-header">
        <div className="header-top">
          <button 
            className="btn-back" 
            onClick={() => navigate('/alimenti-commerciali')}
            title="Torna alla vista Conteggio"
          >
            ← Conteggio
          </button>
          <button 
            className="btn-back secondary" 
            onClick={() => navigate('/')}
            title="Torna alla Dashboard"
          >
            🏠 Dashboard
          </button>
        </div>
        <div className="header-content-main">
          <div className="header-text">
            <h1>Dettaglio Alimenti Commerciali</h1>
            <p className="page-description">
              Analisi dettagliata degli utilizzi di alimenti commerciali con informazioni nutrizionali e dati utenti
            </p>
          </div>
          
          <div className="header-actions">
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/alimenti-commerciali')}
              title="Vai alla vista conteggio"
            >
              📊 Conteggio
            </button>
            
            <button 
              className="btn-header-action" 
              onClick={() => navigate('/alimenti-commerciali/dettaglio')}
              disabled={true}
              title="Sei già nella vista Dettaglio"
            >
              🔍 Dettaglio
            </button>
          </div>
        </div>
      </div>

      {/* Sezione Filtri */}
      <div className="filtri-section">
        <Accordion 
          title="Filtri di Ricerca"
          isOpen={filtersAccordion.isOpen}
          onToggle={filtersAccordion.toggle}
        >
          <div className="filtri-grid">
            <div className="filtro-item">
              <label htmlFor="dataDa">Data Da:</label>
              <input
                type="date"
                id="dataDa"
                value={filtri.dataDa}
                onChange={(e) => handleFiltroChange('dataDa', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="dataA">Data A:</label>
              <input
                type="date"
                id="dataA"
                value={filtri.dataA}
                onChange={(e) => handleFiltroChange('dataA', e.target.value)}
              />
            </div>

            <div className="filtro-item">
              <label htmlFor="categoria">Categoria:</label>
              <select
                id="categoria"
                value={filtri.idCategoria || ''}
                onChange={(e) => handleFiltroChange('idCategoria', e.target.value ? parseInt(e.target.value) : null)}
              >
                <option value="">Tutte le categorie</option>
                {categorie.map(cat => (
                  <option key={cat.id} value={cat.id}>{cat.descrizione}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="filtri-actions">
            <button 
              className="btn-primary" 
              onClick={cercaAlimenti}
              disabled={loading}
            >
              {loading ? 'Ricerca in corso...' : 'Cerca'}
            </button>
          </div>
        </Accordion>
      </div>

      {/* Statistiche Rapide */}
      {risultati.length > 0 && (
        <div className="statistiche-section">
          <h2>Statistiche</h2>
          <div className="stats-grid">
            <div className="stat-card">
              <div className="stat-value">{totaleUtilizzi}</div>
              <div className="stat-label">Totale Utilizzi</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{utentiUnici}</div>
              <div className="stat-label">Utenti Coinvolti</div>
            </div>
            <div className="stat-card">
              <div className="stat-value">{alimentiUnici}</div>
              <div className="stat-label">Alimenti Diversi</div>
            </div>
          </div>
        </div>
      )}

      {/* Messaggio di errore */}
      {error && (
        <div className="error-message">
          <p>⚠️ {error}</p>
        </div>
      )}

      {/* Tabella Risultati */}
      <div className="risultati-section">
        <h2>Risultati Dettagliati</h2>
        
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Caricamento dati...</p>
          </div>
        )}

        {!loading && risultati.length === 0 && !error && (
          <div className="no-results">
            <p>Nessun risultato trovato per i filtri selezionati.</p>
            <p>Prova a modificare i criteri di ricerca.</p>
          </div>
        )}

        {!loading && risultati.length > 0 && (
          <>
            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showBottomControls={false}
            />

            <div className="table-container">
              <table className="risultati-table dettaglio">
                <thead>
                  <tr>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('descrizione')}
                      title="Clicca per ordinare"
                    >
                      Alimento <SortIcon field="descrizione" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('nomeCompleto')}
                      title="Clicca per ordinare"
                    >
                      Utente <SortIcon field="nomeCompleto" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('dataUtilizzo')}
                      title="Clicca per ordinare"
                    >
                      Data Utilizzo <SortIcon field="dataUtilizzo" sortConfig={sortConfig} />
                    </th>
                    <th 
                      className="sortable"
                      onClick={() => handleSort('tipoUtilizzo')}
                      title="Clicca per ordinare"
                    >
                      Tipo <SortIcon field="tipoUtilizzo" sortConfig={sortConfig} />
                    </th>
                    <th>Informazioni</th>
                    <th>Valori Nutrizionali</th>
                    <th>Immagini</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedData.map((item, index) => (
                    <tr key={`${item.idAlimento}-${item.idUser}-${index}`}>
                      <td className="alimento-info">
                        <div className="alimento-main">
                          <strong>{item.descrizione}</strong>
                        </div>
                        <div className="alimento-details">
                          <small>
                            {item.produttore && <span>🏭 {item.produttore}</span>}
                            {item.categoria && <span>📂 {item.categoria}</span>}
                            {item.pacchetto && <span>📦 {item.pacchetto}</span>}
                            {item.cod && <span>🔖 {item.cod}</span>}
                          </small>
                        </div>
                      </td>
                      <td className="utente-info">
                        <div className="utente-main">
                          <strong>{item.nomeCompleto}</strong>
                        </div>
                        <div className="utente-details">
                          <small>
                            👤 ID: {item.idUser}<br/>
                            ✉️ {item.email}
                          </small>
                        </div>
                      </td>
                      <td className="data-utilizzo">
                        <span className="data">{formatDate(item.dataUtilizzo)}</span>
                      </td>
                      <td className="tipo-utilizzo">
                        <span className={`badge tipo-${item.tipoUtilizzo.toLowerCase()}`}>
                          {item.tipoUtilizzo === 'Dieta' ? '🍽️' : '🥘'} {item.tipoUtilizzo}
                        </span>
                      </td>
                      <td className="informazioni">
                        <div className="info-grid">
                          <span title="Kilocalorie">🔥 {formatNutritional(item.kcal)} kcal</span>
                          <span title="Numero utilizzi">📊 {item.numero} utilizzi</span>
                          <span title="ID Alimento">🆔 {item.idAlimento}</span>
                        </div>
                      </td>
                      <td className="valori-nutrizionali">
                        <div className="nutrienti-grid">
                          <div className="nutriente" title="Proteine">
                            <span className="label">P:</span>
                            <span className="value">{formatNutritional(item.proteine)}g</span>
                          </div>
                          <div className="nutriente" title="Lipidi">
                            <span className="label">L:</span>
                            <span className="value">{formatNutritional(item.lipidi)}g</span>
                          </div>
                          <div className="nutriente" title="Carboidrati">
                            <span className="label">C:</span>
                            <span className="value">{formatNutritional(item.carboidrati)}g</span>
                          </div>
                          <div className="nutriente" title="Fibra">
                            <span className="label">F:</span>
                            <span className="value">{formatNutritional(item.fibra)}g</span>
                          </div>
                        </div>
                      </td>
                      <td className="immagini-cell">
                        <div className="immagini-container">
                          {item.pic && (
                            <div className="image-wrapper">
                              <img 
                                src={item.pic} 
                                alt={`Immagine principale di ${item.descrizione}`}
                                className="thumbnail clickable"
                                onClick={() => openImageModal(item.pic!, item.descrizione, item.pic2)}
                                onError={(e) => {
                                  (e.target as HTMLImageElement).style.display = 'none';
                                }}
                              />
                              {item.pic2 && (
                                <div className="image-count-badge" title="Clicca per vedere tutte le immagini">
                                  2 📷
                                </div>
                              )}
                            </div>
                          )}
                          {!item.pic && (
                            <div className="no-image-placeholder">
                              📷
                            </div>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <PaginationControls
              paginationInfo={paginationInfo}
              onGoToPage={goToPage}
              onGoToFirstPage={goToFirstPage}
              onGoToPreviousPage={goToPreviousPage}
              onGoToNextPage={goToNextPage}
              onGoToLastPage={goToLastPage}
              onChangeItemsPerPage={changeItemsPerPage}
              showTopControls={false}
            />
          </>
        )}
      </div>

      {/* Modal per le immagini */}
      {modalImage && (
        <div className="image-modal-overlay" onClick={closeImageModal}>
          <div className="image-modal-content" onClick={(e) => e.stopPropagation()}>
            <button className="modal-close" onClick={closeImageModal} title="Chiudi (ESC)">
              ✕
            </button>
            
            <div className="modal-image-container">
              <img 
                src={modalImage.src} 
                alt={modalImage.alt}
                className="modal-image"
                onError={(e) => {
                  (e.target as HTMLImageElement).src = '/placeholder-image.png';
                }}
              />
              
              {modalImage.extraImages && modalImage.extraImages.length > 0 && (
                <div className="modal-extra-images">
                  <h4>Immagini aggiuntive:</h4>
                  <div className="extra-images-grid">
                    {modalImage.extraImages.map((imgSrc, index) => (
                      <img 
                        key={index}
                        src={imgSrc} 
                        alt={`${modalImage.alt} - Immagine ${index + 2}`}
                        className="extra-image"
                        onClick={() => setModalImage({...modalImage, src: imgSrc})}
                        onError={(e) => {
                          (e.target as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
            
            <div className="modal-info">
              <h3>{modalImage.alt}</h3>
              <p>Clicca fuori dall'immagine o premi ESC per chiudere</p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AlimentiCommercialiDettaglio;